import React from 'react'
import { useTranslation } from 'react-i18next'

import { SectionHeader } from '@/components/Sidebar/Sidebar.styled'
import SidebarGroup from '@/components/Sidebar/SidebarGroup/SidebarGroup'
import SidebarItem from '@/components/Sidebar/SidebarItem/SidebarItem'
import { useNotificationDrawerStore } from '@/features/notification'
import { SidebarSectionConfig } from '@/features/sidebar/types'

export interface SidebarSectionProps {
  config: SidebarSectionConfig
  isCollapsed?: boolean
}

const SidebarSection: React.FC<SidebarSectionProps> = ({
  config,
  isCollapsed = false,
}) => {
  const { t } = useTranslation()
  const { openDrawer } = useNotificationDrawerStore()

  // Type guard to check if item is a group (has children)
  const isGroup = (
    item: SidebarSectionConfig['items'][number]
  ): item is Extract<
    SidebarSectionConfig['items'][number],
    { children: unknown }
  > => {
    return 'children' in item
  }

  const handleNotificationsClick = () => {
    openDrawer()
  }

  return (
    <nav role="navigation" aria-label={config.title}>
      {/* Section Title */}
      {config.title && !isCollapsed && (
        <SectionHeader>{t(config.title)}</SectionHeader>
      )}

      {/* Section Items */}
      <nav>
        {config.items.map((item) => {
          if (isGroup(item)) {
            return (
              <SidebarGroup
                key={item.id}
                config={item}
                isCollapsed={isCollapsed}
              />
            )
          } else {
            return (
              <SidebarItem
                key={item.id}
                config={item}
                isCollapsed={isCollapsed}
                onClick={
                  item.id === 'notifications'
                    ? handleNotificationsClick
                    : undefined
                }
              />
            )
          }
        })}
      </nav>
    </nav>
  )
}

SidebarSection.displayName = 'SidebarSection'

export default React.memo(SidebarSection, (prevProps, nextProps) => {
  return (
    prevProps.config === nextProps.config &&
    prevProps.isCollapsed === nextProps.isCollapsed
  )
})
